import { useCallback, useEffect, useState } from "react";
import Button from "../../components/common/Button";
import {
  FiClock,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronRight,
  FiAlertCircle,
  FiCalendar,
  FiRefreshCw
} from "react-icons/fi";
import reportService from "../../services/reportService";
import vendorService from "../../services/vendorService";
import alertService from "../../utils/alertService";
import SearchableDropdown from "../../components/common/SearchableDropdown";

function getCurrencySymbol(item) {
  return item?.currencySymbol || "";
}

function formatCurrency(value, symbol = "$") {
  const num = Number(value || 0);
  return `${symbol} ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatRupees(value) {
  const num = Number(value || 0);
  return `₹ ${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function APAgingReportPage() {
  const [summary, setSummary] = useState([]);
  const [grandTotal, setGrandTotal] = useState({
    current: 0,
    days1_30: 0,
    days31_60: 0,
    days61_90: 0,
    days91_over: 0,
    totalRemainingAmount: 0
  });
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedVendors, setExpandedVendors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");

  // Load vendors list for filter dropdown
  useEffect(() => {
    async function loadVendors() {
      try {
        const res = await vendorService.getActiveVendors();
        const vendorList = res?.items || res?.data || (Array.isArray(res) ? res : []);
        setVendors(vendorList);
      } catch (err) {
        console.error("Failed to load vendors:", err);
      }
    }
    loadVendors();
  }, []);

  // Fetch Aging Report Data
  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const params = {};
      if (selectedVendorId) params.vendorId = selectedVendorId;
      if (searchTerm) params.search = searchTerm;

      const data = await reportService.getAPAgingReport(params);
      setSummary(data.summary || []);
      setGrandTotal(
        data.grandTotal || {
          current: 0,
          days1_30: 0,
          days31_60: 0,
          days61_90: 0,
          days91_over: 0,
          totalRemainingAmount: 0
        }
      );
    } catch (err) {
      console.error("Error fetching AP aging report:", err);
      setError(err?.message || "Failed to load Aging Report.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedVendorId, searchTerm]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const toggleVendorExpand = (vendorKey) => {
    setExpandedVendors((prev) => ({
      ...prev,
      [vendorKey]: !prev[vendorKey]
    }));
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    try {
      const params = {};
      if (selectedVendorId) params.vendorId = selectedVendorId;
      if (searchTerm) params.search = searchTerm;

      const blob = await reportService.downloadAPAgingReport(format, params);
      const ext = format === "excel" ? "xlsx" : format;
      downloadBlob(blob, `ap-aging-report.${ext}`);
      alertService.success(`Report downloaded successfully (${format.toUpperCase()})`);
    } catch (err) {
      alertService.error(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  // Calculate aggregate priceInRS by aging period for grand total
  const bucketRupeesTotals = summary.reduce(
    (acc, row) => {
      if (row.bills && Array.isArray(row.bills)) {
        row.bills.forEach((b) => {
          const bucketKey = b.bucket || "current";
          if (acc[bucketKey] !== undefined) {
            acc[bucketKey] += Number(b.priceInRS || 0);
          }
        });
      }
      return acc;
    },
    { current: 0, days1_30: 0, days31_60: 0, days61_90: 0, days91_over: 0 }
  );
  const totalRupeesAP = grandTotal.priceInRS || grandTotal.priceInRupees || grandTotal.priceInRs || 0;

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4 space-y-4">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-200/80">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
              <FiClock className="w-4 h-4" />
            </span>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">
              Accounts Payable Aging
            </h1>
          </div>
          <p className="text-xs text-slate-500 pl-8">
            Track outstanding purchase bill balances grouped vendor-wise across aging periods.
          </p>
        </div>

        {/* Download & Refresh Action Buttons */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={fetchReport}
            isLoading={isLoading}
            className="col-span-2 sm:col-auto"
          >
            <span className="flex items-center justify-center gap-1.5">
              <i className="material-icons text-sm">refresh</i>
              <span className="sm:inline">Refresh</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload("xlsx")}
            isLoading={downloadingFormat === "xlsx"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-green-600">
              <i className="material-icons text-sm">grid_on</i>
              <span className="text-[10px] sm:text-xs">EXCEL</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-red-600">
              <i className="material-icons text-sm">picture_as_pdf</i>
              <span className="text-[10px] sm:text-xs">PDF</span>
            </span>
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
          {/* Vendor Filter */}
          <div className="min-w-[190px] w-full sm:w-auto">
            <SearchableDropdown
              name="vendorId"
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              options={[
                { value: "", label: "All Vendors" },
                ...vendors.map((v) => ({
                  value: v.vendorId || v.id,
                  label: v.vendorName || v.name || "Unnamed Vendor"
                }))
              ]}
              placeholder="All Vendors"
              icon={FiFilter}
              buttonClassName="w-full px-2.5 py-1.5 bg-slate-50 border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
            />
          </div>

          {/* Search Term Filter */}
          <div className="relative min-w-[220px] w-full sm:w-auto">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search vendor or bill no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            />
          </div>
        </div>

        {(selectedVendorId || searchTerm) && (
          <button
            onClick={() => {
              setSelectedVendorId("");
              setSearchTerm("");
            }}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium hover:underline self-end sm:self-center"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 flex items-center gap-2">
          <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-medium">{error}</span>
        </div>
      )}

      {/* Main Data Table */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 z-10 bg-slate-100 shadow-sm">
              <tr className="bg-slate-100 text-slate-700 text-[11px] font-bold uppercase tracking-wide border-b border-slate-200">
                <th className="py-2.5 px-3 bg-slate-100 sticky top-0">Vendor Name</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">Current</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">1-30 Days</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">31-60 Days</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">61-90 Days</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">91 Days & Over</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">Total Remaining</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200/70 text-xs text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    <div className="inline-flex items-center gap-2">
                      <FiRefreshCw className="animate-spin w-4 h-4 text-indigo-600" />
                      <span>Loading Aging Report data...</span>
                    </div>
                  </td>
                </tr>
              ) : summary.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No outstanding purchase bills found for the selected criteria.
                  </td>
                </tr>
              ) : (
                summary.map((row, idx) => {
                  const vendorKey = row.vendorId || `row-${idx}`;
                  const isExpanded = Boolean(expandedVendors[vendorKey]);
                  const vendorSymbol = row.bills && row.bills[0] ? getCurrencySymbol(row.bills[0]) : "$";

                  const vendorBucketRupees = {
                    current: 0,
                    days1_30: 0,
                    days31_60: 0,
                    days61_90: 0,
                    days91_over: 0
                  };
                  if (row.bills && Array.isArray(row.bills)) {
                    row.bills.forEach((b) => {
                      const bucketKey = b.bucket || "current";
                      if (vendorBucketRupees[bucketKey] !== undefined) {
                        vendorBucketRupees[bucketKey] += Number(b.priceInRS || 0);
                      }
                    });
                  }
                  const vendorTotalRupees = row.priceInRS || row.priceInRupees || row.priceInRs || 0;

                  return (
                    <tr key={vendorKey} className="contents">
                      {/* Vendor Group Row */}
                      <tr
                        onClick={() => toggleVendorExpand(vendorKey)}
                        className="hover:bg-slate-50/80 cursor-pointer transition-colors font-medium border-b border-slate-100"
                      >
                        <td className="py-2.5 px-3 font-semibold text-slate-800 flex items-center gap-2">
                          <span className="text-slate-400 hover:text-slate-600 transition-colors">
                            {isExpanded ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
                          </span>
                          {row.vendorName}
                          {row.bills?.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full font-normal">
                              {row.bills.length} {row.bills.length === 1 ? "bill" : "bills"}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-emerald-700">
                          {vendorBucketRupees.current > 0 ? formatRupees(vendorBucketRupees.current) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-blue-700">
                          {vendorBucketRupees.days1_30 > 0 ? formatRupees(vendorBucketRupees.days1_30) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-amber-700">
                          {vendorBucketRupees.days31_60 > 0 ? formatRupees(vendorBucketRupees.days31_60) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-orange-700">
                          {vendorBucketRupees.days61_90 > 0 ? formatRupees(vendorBucketRupees.days61_90) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-rose-700">
                          {vendorBucketRupees.days91_over > 0 ? formatRupees(vendorBucketRupees.days91_over) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-bold text-slate-900 bg-slate-50/50">
                          {formatRupees(vendorTotalRupees)}
                        </td>
                      </tr>

                      {/* Expandable Bill Details Table */}
                      {isExpanded && row.bills?.length > 0 && (
                        <tr className="bg-slate-50/50">
                          <td colSpan={7} className="p-3 pl-8 border-b border-slate-200">
                            <div className="bg-white rounded-lg p-2.5 border border-slate-200 shadow-inner overflow-hidden">
                              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                <FiCalendar className="w-3 h-3" />
                                Individual Bills for {row.vendorName}
                              </h4>
                              <div className="overflow-x-auto max-h-[320px] overflow-y-auto custom-scrollbar">
                                <table className="w-full text-[11px] text-left">
                                  <thead className="sticky top-0 z-10 bg-slate-100">
                                    <tr className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Bill No</th>
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Booking No</th>
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Bill Date</th>
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Due Date</th>
                                      <th className="py-1.5 px-2.5 text-right bg-slate-100 sticky top-0">Total Amount (₹)</th>
                                      <th className="py-1.5 px-2.5 text-right bg-slate-100 sticky top-0">Paid Amount (₹)</th>
                                      <th className="py-1.5 px-2.5 text-right bg-slate-100 sticky top-0">Remaining (₹)</th>
                                      <th className="py-1.5 px-2.5 text-center bg-slate-100 sticky top-0">Aging Category</th>
                                     </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {row.bills.map((bill) => {
                                      const rateVal = Number(bill.dollarRate || 1);
                                      const totalInRs = Number(((bill.totalAmount || 0) * rateVal).toFixed(2));
                                      const paidInRs = Number(((bill.paidAmount || 0) * rateVal).toFixed(2));
                                      const remainingInRs = bill.priceInRS !== undefined ? bill.priceInRS : Number(((bill.remainingAmount || 0) * rateVal).toFixed(2));

                                      return (
                                        <tr key={bill.purchaseMasterId || bill.billNo} className="hover:bg-slate-50">
                                          <td className="py-1.5 px-2.5 font-semibold text-slate-800">{bill.billNo || "-"}</td>
                                          <td className="py-1.5 px-2.5 text-slate-600">{bill.bookingNo || "-"}</td>
                                          <td className="py-1.5 px-2.5 text-slate-600">
                                            {bill.billDate ? new Date(bill.billDate).toLocaleDateString() : "-"}
                                          </td>
                                          <td className="py-1.5 px-2.5 text-slate-600">
                                            {bill.dueDate ? new Date(bill.dueDate).toLocaleDateString() : "-"}
                                          </td>
                                          <td className="py-1.5 px-2.5 text-right text-slate-700">
                                            {formatRupees(totalInRs)}
                                          </td>
                                          <td className="py-1.5 px-2.5 text-right text-emerald-600">
                                            {formatRupees(paidInRs)}
                                          </td>
                                          <td className="py-1.5 px-2.5 text-right font-bold text-slate-900">
                                            {formatRupees(remainingInRs)}
                                          </td>
                                          <td className="py-1.5 px-2.5 text-center">
                                            <span
                                              className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${
                                                bill.bucket === "current"
                                                  ? "bg-emerald-100 text-emerald-800"
                                                  : bill.bucket === "days1_30"
                                                    ? "bg-blue-100 text-blue-800"
                                                    : bill.bucket === "days31_60"
                                                      ? "bg-amber-100 text-amber-800"
                                                      : bill.bucket === "days61_90"
                                                        ? "bg-orange-100 text-orange-800"
                                                        : "bg-rose-100 text-rose-800"
                                              }`}
                                            >
                                              {bill.bucketName}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Sticky Grand Total Footer */}
            {summary.length > 0 && (
              <tfoot className="sticky bottom-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                <tr className="bg-slate-900 text-white font-bold text-xs border-t-2 border-slate-700">
                  <td className="py-3 px-3 uppercase tracking-wide bg-slate-900 sticky bottom-0">Grand Total</td>
                  <td className="py-3 px-3 text-right bg-slate-900 sticky bottom-0 text-emerald-400 font-bold">
                    {formatRupees(bucketRupeesTotals.current)}
                  </td>
                  <td className="py-3 px-3 text-right bg-slate-900 sticky bottom-0 text-blue-400 font-bold">
                    {formatRupees(bucketRupeesTotals.days1_30)}
                  </td>
                  <td className="py-3 px-3 text-right bg-slate-900 sticky bottom-0 text-amber-400 font-bold">
                    {formatRupees(bucketRupeesTotals.days31_60)}
                  </td>
                  <td className="py-3 px-3 text-right bg-slate-900 sticky bottom-0 text-orange-400 font-bold">
                    {formatRupees(bucketRupeesTotals.days61_90)}
                  </td>
                  <td className="py-3 px-3 text-right bg-slate-900 sticky bottom-0 text-rose-400 font-bold">
                    {formatRupees(bucketRupeesTotals.days91_over)}
                  </td>
                  <td className="py-3 px-3 text-right bg-slate-950 sticky bottom-0 text-white font-bold">
                    {formatRupees(totalRupeesAP)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
